import Dagre from "@dagrejs/dagre";

/**
 * Estimate rendered pixel dimensions of a DeviceNode based on its data.
 *
 * DeviceNode renders:
 *   - Header: icon (20px) + label text (whitespace-nowrap) + gap/padding → ~28px tall
 *   - Field rows: up to 4 main fields (os, hostname, ip_address, category)
 *     each row ~20px tall (text-xs + py-0.5)
 *   - Padding: p-2 (8px each side) + border-b on header (~1px)
 *
 * Width depends on label length and longest field value.
 */
function estimateNodeSize(node) {
  const data = node?.data || {};
  const label = data.label || data.nodeType || "";

  // Count visible main fields (same logic as DeviceNode)
  const mainFields = ["os", "hostname", "ip_address", "category"].filter(
    (k) => !!data[k],
  );
  const fieldCount = mainFields.length;

  // Find the longest field value for width estimation
  const longestValue = mainFields.reduce((max, k) => {
    const val = String(data[k] || "");
    return val.length > max ? val.length : max;
  }, 0);

  // Width: base from label, expand if field values are long
  // ~7px per character at text-xs, plus key column (~80px), padding (16px), icon (28px)
  const labelWidth = label.length * 7 + 28 + 16;
  const fieldWidth = longestValue * 6 + 80 + 16;
  const width = Math.max(180, Math.min(320, Math.max(labelWidth, fieldWidth)));

  // Height: header(28) + fields(fieldCount * 20) + padding(16) + handles(8)
  const height = 28 + Math.max(fieldCount, 1) * 20 + 16 + 8;

  return { width, height };
}

/**
 * Compute dynamic spacing based on topology size.
 * Larger topologies need tighter spacing to stay navigable;
 * smaller ones benefit from more breathing room.
 */
function computeSpacing(nodeCount) {
  if (nodeCount <= 10) return { rankSep: 120, nodeSep: 60 };
  if (nodeCount <= 25) return { rankSep: 100, nodeSep: 50 };
  if (nodeCount <= 50) return { rankSep: 80, nodeSep: 40 };
  return { rankSep: 60, nodeSep: 30 };
}

// Apply dagre layout to nodes and edges
export function applyDagreLayout(nodes, edges, options = {}) {
  if (!nodes || nodes.length === 0) {
    return nodes;
  }

  const {
    direction = "TB",
    rankSep: overrideRankSep,
    nodeSep: overrideNodeSep,
  } = options;

  const autoSpacing = computeSpacing(nodes.length);
  const rankSep = overrideRankSep ?? autoSpacing.rankSep;
  const nodeSep = overrideNodeSep ?? autoSpacing.nodeSep;

  // Pre-compute per-node dimensions
  const nodeSizes = new Map();
  nodes.forEach((node) => {
    nodeSizes.set(node.id, estimateNodeSize(node));
  });

  // Create a new dagre graph
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  g.setGraph({
    rankdir: direction,
    ranksep: rankSep,
    nodesep: nodeSep,
    marginx: 50,
    marginy: 50,
  });

  nodes.forEach((node) => {
    const size = nodeSizes.get(node.id);
    g.setNode(node.id, {
      width: size.width,
      height: size.height,
    });
  });

  // Add edges to the graph — dagre derives ranks from the DAG structure
  edges.forEach((edge) => {
    if (edge.source && edge.target) {
      g.setEdge(edge.source, edge.target);
    }
  });

  // Run the layout algorithm
  Dagre.layout(g);

  // Apply the calculated positions to nodes, using per-node dimensions for centering
  return nodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    const size = nodeSizes.get(node.id);
    if (nodeWithPosition) {
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - size.width / 2,
          y: nodeWithPosition.y - size.height / 2,
        },
      };
    }
    return node;
  });
}

// Layout direction options for UI
export const LAYOUT_DIRECTIONS = [
  { value: "TB", label: "Top to Bottom", icon: "↓" },
  { value: "LR", label: "Left to Right", icon: "→" },
  { value: "BT", label: "Bottom to Top", icon: "↑" },
  { value: "RL", label: "Right to Left", icon: "←" },
];

export default applyDagreLayout;
